/**
 * ScreenHeader - Mobile
 * 
 * Standard back navigation header for stacked screens.
 * Clean, minimal design following land stewardship aesthetic.
 */

import React, { useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, touch, icon } from '../../../shared/tokens/mobile';

interface ScreenHeaderProps {
  title: string;
  onBack: () => void;
  onMore?: () => void;
  loading?: boolean;
}

export function ScreenHeader({ title, onBack, onMore, loading = false }: ScreenHeaderProps) {
  // Wrap onBack to ensure it fires reliably
  const handleBackPress = useCallback(() => {
    if (onBack) {
      onBack();
    }
  }, [onBack]);

  const handleMorePress = useCallback(() => {
    if (onMore) {
      onMore();
    }
  }, [onMore]);

  return (
    <View style={styles.header}>
      <TouchableOpacity 
        onPress={handleBackPress} 
        style={styles.headerButton} 
        activeOpacity={0.6}
        hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
        accessibilityLabel="Go back"
        accessibilityRole="button"
      >
        <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
      </TouchableOpacity>
      
      <View style={styles.headerTitle}>
        {loading ? (
          <ActivityIndicator size="small" color={color.primary} />
        ) : (
          <Text style={styles.headerTitleText} numberOfLines={1}>{title}</Text>
        )}
      </View>
      
      {onMore ? (
        <TouchableOpacity 
          onPress={handleMorePress} 
          style={styles.headerButton}
          activeOpacity={0.6}
          hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
          accessibilityLabel="More options"
          accessibilityRole="button"
        >
          <Ionicons name="ellipsis-horizontal" size={icon.lg} color={color.textPrimary} />
        </TouchableOpacity>
      ) : (
        <View style={styles.headerButton} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: space.sm,
    paddingVertical: space.sm,
    backgroundColor: color.background,
    borderBottomWidth: 1,
    borderBottomColor: color.divider,
  },
  headerButton: {
    width: touch.minimum,
    height: touch.minimum,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    flex: 1,
    paddingHorizontal: space.sm,
  },
  headerTitleText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    textAlign: 'center',
  },
});

